import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRailPhysics } from "@/hooks/useRailPhysics";
import { ThriftPiece } from "./ThriftPiece";
import { ThriftSizeChips, thriftSizes, fitsSize } from "./ThriftSizeChips";
import { isNewSince } from "@/lib/thriftVisit";
import { KIDS_AGES, ageOf, ageLabel } from "@/lib/lamka";

const NavBtn = ({ dir, onClick, disabled, prefix }) => {
  const Icon = dir < 0 ? ChevronLeft : ChevronRight;
  return (
    <button data-testid={`${prefix}-${dir < 0 ? "prev" : "next"}-btn`} aria-label={dir < 0 ? "Previous piece" : "Next piece"} onClick={onClick} disabled={disabled} className={`hidden sm:flex absolute ${dir < 0 ? "left-8" : "right-8"} top-[200px] z-[60] h-12 w-12 rounded-full items-center justify-center bg-white/70 backdrop-blur-md border border-[#0F0F0F]/10 text-[#0F0F0F] hover:border-[#C5A059] disabled:opacity-0 transition-[opacity,border-color]`}>
      <Icon className="h-5 w-5" />
    </button>
  );
};

const useStoredSize = (key) => {
  const [size, setSize] = useState(() => localStorage.getItem(key));
  const set = (s) => {
    setSize(s);
    if (s) localStorage.setItem(key, s);
    else localStorage.removeItem(key);
  };
  return [size, set];
};

const BY_SIZE = { kind: "size", options: thriftSizes, fits: fitsSize, format: (s) => s, allLabel: "All sizes", badge: "New in your size" };
const BY_AGE = {
  kind: "age",
  options: (items) => KIDS_AGES.filter((a) => items.some((it) => ageOf(it.description) === a)),
  fits: (it, a) => !a || ageOf(it.description) === a,
  format: ageLabel,
  allLabel: "All ages",
  badge: "New for this age",
};

export const ThriftRail = ({ items, cutoutFor, onToggleLike, since, prefix, sizeKey, emptyText, byAge = false }) => {
  const f = byAge ? BY_AGE : BY_SIZE;
  const [size, setSize] = useStoredSize(sizeKey);
  const sizes = useMemo(() => f.options(items), [items, f]);
  const active = sizes.find((s) => s.toUpperCase() === (size || "").toUpperCase()) || null;
  const shown = useMemo(() => items.filter((it) => f.fits(it, active)), [items, active, f]);
  const rail = useRailPhysics(shown.length);
  const { goTo } = rail;

  useEffect(() => { goTo(0); }, [active, goTo]);

  if (!items.length) return <p data-testid={`${prefix}-empty`} className="px-4 text-center font-display text-xl text-[#0F0F0F]/55 py-16">{emptyText}</p>;

  return (
    <>
      {sizes.length > 0 && <ThriftSizeChips sizes={sizes} value={active} onChange={setSize} prefix={prefix} kind={f.kind} format={f.format} allLabel={f.allLabel} />}
      <div ref={rail.trackRef} {...rail.handlers} data-testid={`${prefix}-rack`} className="relative h-[540px] sm:h-[590px] select-none touch-pan-y cursor-grab active:cursor-grabbing">
        <div aria-hidden="true" className="absolute inset-x-0 top-[6px] h-[3px] bg-gradient-to-r from-[#C9CDD2] via-[#8D939B] to-[#4B5563] shadow-[0_6px_10px_rgba(60,40,20,0.35)]" />
        {shown.map((it, i) => (
          <ThriftPiece key={it.id} item={it} cutout={cutoutFor(it)} index={i} badge={f.badge} isNew={!!active && isNewSince(it, since)} offset={i - rail.pos} vel={rail.vel} spacing={rail.spacing} onSelect={() => rail.goTo(i)} onToggleLike={onToggleLike} />
        ))}
        {shown.length > 1 && <NavBtn prefix={prefix} dir={-1} onClick={() => rail.goTo(rail.active - 1)} disabled={rail.active === 0} />}
        {shown.length > 1 && <NavBtn prefix={prefix} dir={1} onClick={() => rail.goTo(rail.active + 1)} disabled={rail.active === shown.length - 1} />}
      </div>
      {shown.length > 1 && <p data-testid={`${prefix}-counter`} className="text-center font-mono text-[10px] uppercase tracking-[0.25em] text-[#0F0F0F]/55 mt-2">{rail.active + 1} / {shown.length} · drag or swipe to browse</p>}
    </>
  );
};
